/**
 * runidea.ai — Phase 14 Wave 2 — Module B: Admin Properties.
 *
 * Cross-user properties browser with edit drawer + transfer modal +
 * typed-confirm delete (single or bulk). Uses the AdminDataTable primitive.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AdminDataTable from "@/components/admin/AdminDataTable";
import TypedConfirmDialog from "@/components/admin/TypedConfirmDialog";
import { Building2, Loader2, ArrowRightLeft, Pencil, Trash2 } from "lucide-react";
import { useFmt } from "@/lib/useFmt";

function fmtAddress(p) {
  return [p.address, p.city, p.state].filter(Boolean).join(", ");
}

function EditPropertySheet({ open, onOpenChange, property, onSaved }) {
  const [form, setForm] = useState(() => ({ ...property }));
  const [saving, setSaving] = useState(false);
  // Reset when a new row is opened.
  const propertyId = property?.id;
  useEffect(() => { setForm({ ...(property || {}) }); }, [propertyId, property]);
  if (!property) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      const patch = {
        address: form.address,
        city: form.city,
        state: form.state,
        zip: form.zip,
        country: form.country,
        property_type: form.property_type,
        units: Number(form.units) || 1,
        bedrooms: Number(form.bedrooms) || 0,
        bathrooms: Number(form.bathrooms) || 0,
        square_feet: Number(form.square_feet) || 0,
        asking_price: Number(form.asking_price) || 0,
      };
      const { data } = await api.patch(`/admin/properties/${property.id}`, patch);
      toast.success("Property updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md" data-testid="admin-prop-edit">
        <SheetHeader>
          <SheetTitle>Edit property</SheetTitle>
          <SheetDescription className="font-mono text-[11px] text-slate-400">{property.id}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 grid gap-3">
          {[
            ["Address", "address"], ["City", "city"], ["State", "state"], ["Zip", "zip"], ["Country", "country"],
            ["Property type", "property_type"],
          ].map(([label, key]) => (
            <div key={key}>
              <Label className="text-xs text-slate-600">{label}</Label>
              <Input
                data-testid={`admin-prop-edit-${key}`}
                value={form[key] ?? ""}
                onChange={(e) => set(key, e.target.value)}
                className="mt-1 h-9 text-sm"
              />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Units", "units"], ["Bedrooms", "bedrooms"], ["Bathrooms", "bathrooms"],
              ["Sq ft", "square_feet"], ["Asking price", "asking_price"],
            ].map(([label, key]) => (
              <div key={key}>
                <Label className="text-xs text-slate-600">{label}</Label>
                <Input
                  type="number"
                  data-testid={`admin-prop-edit-${key}`}
                  value={form[key] ?? 0}
                  onChange={(e) => set(key, e.target.value)}
                  className="mt-1 h-9 text-sm tabular-nums"
                />
              </div>
            ))}
          </div>
        </div>
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="admin-prop-edit-save" className="bg-indigo-600 text-white hover:bg-indigo-700">
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function TransferModal({ open, onOpenChange, property, onTransferred }) {
  const [email, setEmail] = useState("");
  const [resolved, setResolved] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const lookup = useCallback(async (val) => {
    setResolved(null);
    if (!val || !val.includes("@")) return;
    setSearching(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: val, limit: 5 } });
      const exact = (data?.items || []).find((u) => u.email?.toLowerCase() === val.toLowerCase());
      setResolved(exact || null);
    } finally {
      setSearching(false);
    }
  }, []);

  const submit = async () => {
    if (!resolved || !property) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/properties/${property.id}/transfer`, { new_owner_id: resolved.id });
      toast.success(`Property transferred. ${data.deals_reassigned} deals reassigned.`);
      onTransferred?.();
      onOpenChange(false);
      setEmail("");
      setResolved(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-prop-transfer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-indigo-600" /> Transfer property</DialogTitle>
          <DialogDescription>
            Reassign <span className="font-mono text-xs">{property?.address}</span> to a different owner. Every deal on this property will be reassigned too.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">New owner email</Label>
            <Input
              data-testid="admin-prop-transfer-email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); lookup(e.target.value); }}
              placeholder="owner@example.com"
              className="mt-1 h-9 text-sm"
            />
          </div>
          {searching ? (
            <p className="text-xs text-slate-500"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Looking up user…</p>
          ) : email && !resolved ? (
            <p className="text-xs text-amber-700">No user matches that email exactly.</p>
          ) : resolved ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900" data-testid="admin-prop-transfer-resolved">
              <strong>{resolved.name || resolved.email}</strong>
              <span className="ml-2 font-mono text-[10px]">{resolved.id}</span>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!resolved || submitting}
            data-testid="admin-prop-transfer-submit"
            className="bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {submitting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProperties() {
  const fmt = useFmt();
  const [editing, setEditing] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [deleting, setDeleting] = useState(null);   // single id
  const [bulkPending, setBulkPending] = useState(null); // { ids, clear }
  const [refresh, setRefresh] = useState(0);

  const fetchPage = useCallback(async ({ skip, limit, q }) => {
    const { data } = await api.get("/admin/properties", { params: { skip, limit, q } });
    return data;
    // include `refresh` so the table re-fetches when we mutate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const doDelete = async (id) => {
    try {
      await api.delete(`/admin/properties/${id}`);
      toast.success("Property soft-deleted");
      setDeleting(null);
      setRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const doBulkDelete = async () => {
    if (!bulkPending?.ids?.length) return;
    try {
      const { data } = await api.post("/admin/properties/bulk-delete", { ids: bulkPending.ids });
      toast.success(`Deleted ${data.deleted} properties (${data.deals_soft_deleted} deals cascaded)`);
      bulkPending.clear?.();
      setBulkPending(null);
      setRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const columns = [
    { key: "address", label: "Property", render: (r) => (
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
          <span className="truncate font-medium text-slate-900">{fmtAddress(r)}</span>
        </div>
        <div className="font-mono text-[10px] text-slate-400">{r.id}</div>
      </div>
    )},
    { key: "property_type", label: "Type", render: (r) => (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
        {(r.property_type || "?").replace(/_/g, " ")}
      </span>
    )},
    { key: "units", label: "Units", align: "right" },
    { key: "asking_price", label: "Price", align: "right", render: (r) => fmt.money(r.asking_price) },
    { key: "user_id", label: "Owner", render: (r) => (
      <span className="font-mono text-[10px] text-slate-500">{(r.user_id || "").slice(0, 8)}</span>
    )},
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Module B</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Properties</h1>
        <p className="mt-1 text-sm text-slate-500">Cross-user property browser. Edit, transfer ownership, or soft-delete.</p>
      </div>

      <AdminDataTable
        testid="admin-properties"
        columns={columns}
        fetchPage={fetchPage}
        searchPlaceholder="Search by address or city…"
        emptyMessage="No properties match the filters."
        bulkActions={[
          { label: "Bulk delete", variant: "danger", onClick: (ids, clear) => setBulkPending({ ids, clear }) },
        ]}
        rowActions={(row) => [
          { label: "Edit", variant: "primary", onClick: setEditing },
          { label: "Transfer", onClick: setTransferring },
          { label: "Delete", variant: "danger", onClick: (r) => setDeleting(r) },
        ]}
      />

      <EditPropertySheet
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        property={editing}
        onSaved={() => setRefresh((n) => n + 1)}
      />
      <TransferModal
        open={!!transferring}
        onOpenChange={(v) => !v && setTransferring(null)}
        property={transferring}
        onTransferred={() => setRefresh((n) => n + 1)}
      />
      <TypedConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Soft-delete this property?"
        description={deleting ? `${fmtAddress(deleting)} — all attached deals will be soft-deleted too.` : ""}
        onConfirm={() => deleting && doDelete(deleting.id)}
        testid="admin-prop-delete"
      />
      <TypedConfirmDialog
        open={!!bulkPending}
        onOpenChange={(v) => !v && setBulkPending(null)}
        title={`Soft-delete ${bulkPending?.ids?.length || 0} properties?`}
        description="Each property and all attached deals will be soft-deleted. This will be audit-logged per id."
        onConfirm={doBulkDelete}
        testid="admin-prop-bulkdelete"
      />
    </div>
  );
}
